import React from 'react';
import {Field} from 'redux-form';

import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import {DataMap} from '@grapecity/wijmo.grid';

import {
  renderTextField,
  renderSelect,
  renderCheckbox,
  renderRadioGroup,
  renderLink,
  renderSelectMultiLine,
} from '../components/atoms/CustomPart.jsx';
import {
  PositiveButton,
  PositiveButton2,
} from '../components/atoms/Buttons/PositiveButton';
import * as validateRule from '../components/validateRule';
import Upload from '../components/organisms/UploadCustom';
import {
  findIndex,
  getFilteringMap,
  formatStringDate,
  ISO_LOCAL_DATE,
  formatStringToPrice,
} from '../common/common';
import {
  CATEGORY_TYPE,
  IDO_TYPE,
  DNT_ID_TYPE,
  BUNKITEN_MITOROKU_TYPE,
  ORDER_CATEGORY,
  ROLE_ID,
  GRID_STATUS,
  KASETU_SETUBI_CD,
  KASETU_SETUBI_CD_NTT,
  KASETU_SETUBI_TYPE,
} from '../common/commonConst';
import {FormControlLabel, Link, Radio, TextareaAutosize} from '@mui/material';
import NegativeButton from '../components/atoms/Buttons/NegativeButton';
import MuiTooltip from '../components/atoms/MuiTooltip';


/**
 * 基本情報表示部の申込基本情報を取得
 * @param {*} userInfo ユーザ情報
 * @param {*} orderData 申込情報
 * @return {*} 基本情報表示部の申込基本情報
 */
export function getOrderInfomationValue(
    userInfo, orderData) {
  let orderInformation = [
    {binding: 'orderNo', name: '申込番号', value: ''},
    {binding: 'rev', name: '版数', value: ''},
    {binding: 'orderKyogasya', name: '共架者コード', value: ''},
    {binding: 'orderZigyosyaName', name: '共架事業者', value: ''},
    {binding: 'orderUketukeKyogasya', name: '申込先共架者コード', value: ''},
    {binding: 'orderUketukeZigyosyaName', name: '申込先共架事業者', value: ''},
    {binding: 'orderDate', name: '申込日', value: '', format: 'date'},
    {binding: 'orderTorokuDate', name: '申込登録日', value: '', format: 'date'},
    {binding: 'kyogaTantoKasyo', name: '共架担当箇所', dataMap: null, value: ''},
    {binding: 'kyogaTantosyaName', name: '共架担当者', value: ''},
    {binding: 'kojiKenmei', name: '工事件名', value: ''},
    {binding: 'kojibasyoZigyojoName', name: '工事場所/事業場', value: ''},
    {binding: 'kojibasyoDaihyoDntNo', name: '工事場所/代表電柱番号', value: ''},
    {binding: 'kojibasyoPrefecturesName', name: '工事場所/都道府県', value: ''},
    {binding: 'kojibasyoMunicipalitiesName', name: '工事場所/市区群町村', value: ''},
    {binding: 'kojibasyoOazatusyoName', name: '工事場所/大字通称', value: ''},
    {binding: 'kojibasyoAzatyomeName', name: '工事場所/字丁目', value: ''},
    {binding: 'kojibasyoBirumei', name: '工事場所/ビル名等', value: ''},
    {binding: 'sisetuYearMonth', name: '施設年月', value: '', format: 'month'},
  ];
  const isKyogasya = (userInfo.yakuwariRoleId === ROLE_ID.KYOGASYA);
  const isOrder = (orderData.orderCategory === ORDER_CATEGORY.ORDER);
  const isZeseiOrder = (!!orderData.zeseiFlag);
  const isTasyatyuOrder =
    (orderData.orderCategory === ORDER_CATEGORY.TASYATYU_ORDER);
  const isKgtuOrder =
    (orderData.orderCategory === ORDER_CATEGORY.KYOGATYU_TOROKU);
  if (isKyogasya) {
    // 共架者利用時
    orderInformation = orderInformation.filter((info) => info.binding != 'kyogaTantoKasyo' && info.binding != 'kyogaTantosyaName');
  }
  if (!isTasyatyuOrder) {
    // 申込区分が「他社柱申込」の場合、表示
    orderInformation = orderInformation.filter((info) => info.binding != 'orderUketukeKyogasya' && info.binding != 'orderUketukeZigyosyaName');
  }
  if (isTasyatyuOrder) {
    // 申込区分が「他社柱申込」の場合、非表示
    orderInformation = orderInformation.filter((info) => info.binding != 'kyogaTantoKasyo' && info.binding != 'kyogaTantosyaName');
  }
  if (!isOrder && !isTasyatyuOrder) {
    // 申込区分が「共架申込」、「他社柱申込」の場合、表示
    orderInformation = orderInformation.filter((info) => info.binding != 'orderDate' && info.binding != 'orderTorokuDate');
  }
  if ((isOrder && !isZeseiOrder) || isKgtuOrder) {
    orderInformation = orderInformation.filter((info) => info.binding != 'sisetuYearMonth');
  }
  for (const orderInfo of orderInformation) {
    const binding = orderInfo.binding;
    if (Object.hasOwn(orderData, binding)) {
      orderInfo.value = orderData[binding];
    }
  }
  return orderInformation;
}

// Model定義がBoolean項目
const typeBooleanItems = [
  'senkoKentyu',
  'kyogiKanryoFlag',
  'tatiagariTyu',
  'gijyutuKentoHiyoSeikyuFlag',
  'genmenTaisyo',
  'masterUpdateFlag',
  'isChangedTushin',
  'isChangedKiki',
  'isOldContractIssokukaMoto',
  'isOldContractIssokuka1',
  'isOldContractIssokuka2',
  'isOldContractIssokuka3',
  'isOldContractIssokuka4',
];

// Model定義が数値系項目
const typeNumberItems = [
  'bukkenNo',
  'bukkenNoBukkenMaster',
  'edaban',
  'edabanHenkogo',
  'tyutyo',
  'jyosu',
  'gaikewaTosyo',
  'gaikewaSyorai',
  'henatukiKuriage',
  'densenCableKuriage',
  'densenCableKurisage',
  'kaisyuKojiIraiIdo',
  'kaisyuKojiIdoHaiden',
  'kaisyuKojiIdoTusin',
  'kaisyuKojiKasanHiyoSekkei',
  'kaisyuKojiKasanHiyoJisseki',
  'kojiHiyo2024',
  'kojiHiyoHenkogo2024',
  'kaisyuKojiMitumori',
  'kaisyuKojiKakutei',
  'sekkeiKaju',
  'issokukaCategory',
  'tijyoHeight',
  'tijyoHeightSyunko',
  'haidenSetubiRikaku',
  'haidenSetubiRikakuSyunko',
  'tusinsenRikakuJyoi',
  'tusinsenRikakuJyoiSyunko',
  'tusinsenRikakuKai',
  'tusinsenRikakuKaiSyunko',
  'kaiheikiDaisu',
  'safetyRateF1',
  'safetyRateF2',
  'safetyRateF3',
  'safetyRateF4',
  'safetyRateF5',
  'safetyRateF1Genba',
  'safetyRateF2Genba',
  'safetyRateF3Genba',
  'safetyRateF4Genba',
  'safetyRateF5Genba',
  'kaisyuMaeRikaku',
  'kaisyuGoRikaku',
  'bukkenNoKeikan',
  'displayOrder',
  'kyogaBukkenId',
  'edaban1',
  'bukkenNo1',
  'bukkenNo1BukkenMaster',
  'edaban2',
  'bukkenNo2',
  'bukkenNo2BukkenMaster',
  'keikanM',
  'motoKakudo',
  'gaikei',
  'jijyu',
  'tido',
  'keikanRikakuTeiatuZetuen',
  'keikanRikakuHikikomi',
  'keikanRikakuKouatuZetuen',
  'keikanRikakuTusin',
  'keikanRikakuLCableHZetuen',
  'keikanRikakuKouatuCable',
  'keikanMSyunko',
  'motoKakudoSyunko',
  'gaikeiSyunko',
  'jijyuSyunko',
  'tidoSyunko',
  'keikanRikakuTeiatuZetuenSyunko',
  'keikanRikakuHikikomiSyunko',
  'keikanRikakuKouatuZetuenSyunko',
  'keikanRikakuTusinSyunko',
  'keikanRikakuLCableHZetuenSyunko',
  'keikanRikakuKouatuCableSyunko',
  'bunkitenId',
  'kijyunDntEdaban',
  'hoikaku',
  'kyori',
  'ido',
  'keido',
  'idoByDntMst',
  'keidoByDntMst',
  'sekkeiKajyu',
  'kgbkId',
  'orgTijyoHeightSyunko',
  'kaisyuKojiBaseHiyoHaidenSekkei',
  'kaisyuKojiBaseHiyoHaidenJisseki',
  'kaisyuKojiKasanHiyoHaidenSekkei',
  'kaisyuKojiKasanHiyoHaidenJisseki',
  'kaisyuKojiBaseHiyoTusinSekkei',
  'kaisyuKojiBaseHiyoTusinJisseki',
  'kaisyuKojiHiyoSekkei',
  'kaisyuKojiHiyoJisseki',
  'unitPrice',
  'settiTeikoKyoyoutiA',
  'settiTeikoKyoyoutiB',
  'settiTeikoSokuteitiA',
  'settiTeikoSokuteitiB',
  'henatukiKvaDai',
];

/**
 * 保存用にフォーマット変換
 * Boolean項目の場合、空値はfalseに変換
 * 数値系項目の場合、空値はnullに変換
 * @param {array} dataItems
 * @param {array} dateItemsKey 日付型項目のkey
 * @return {array} 保存用リスト
 */
export function convertSaveList(dataItems, dateItemsKey = []) {
  let list = [];
  let rowIndex = 0;
  for (const dataItem of dataItems) {
    let _item = {...dataItem};
    if (_item.gridStatus !== GRID_STATUS.DELETE) {
      rowIndex++;
      _item['displayOrder'] = rowIndex;
    }
    for (const key of Object.keys(_item)) {
      // Boolean項目
      if (typeBooleanItems.includes(key)) {
        _item[key] = (_item[key] === '' ? false : _item[key]);
      }
      if (typeNumberItems.includes(key)) {
        _item[key] = (_item[key] === '' ? null : _item[key]);
      }
      if (dateItemsKey.includes(key)) {
        _item[key] = formatStringDate(_item[key], ISO_LOCAL_DATE);
      }
    }
    list.push(_item);
  }
  return list;
}

/**
 * 電柱選択の返却値から、電柱ID種別を判定する。
 * @param {object} data 電柱選択の戻り
 * @return {string} 電柱ID種別
 */
export function getIdType(data) {
  return data.infoType === 'B' ?
    Object.hasOwn(data, 'type') ? DNT_ID_TYPE.BUKKEN_BUNKI : DNT_ID_TYPE.BUKKEN_SETUBI :
    data.infoType === 'D' ? DNT_ID_TYPE.SETUBI :
    data.infoType === 'C' ?
      data.type === BUNKITEN_MITOROKU_TYPE.BUNKI ? DNT_ID_TYPE.BUNKI :
        data.type === BUNKITEN_MITOROKU_TYPE.MITOUROKU ?
          DNT_ID_TYPE.MITOUROKU : DNT_ID_TYPE.OTHER :
    DNT_ID_TYPE.OTHER;
}

export function createFieldItems(gmnItems) {
  let list = [];
  for (const _key of Object.keys(gmnItems)) {
    const gmn = gmnItems[_key];
    if (gmn.label == '') {
      continue;
    }
    let _item = {
      xs: 12, sm: gmn.sm,
      type: gmn.type,
    };
    setItem(gmn, _item);
    list.push(_item);
  }
  return list;
};

export function createGridFields(key, components) {
  const lists = components.map((_item) => {
    let item = {};
    Object.assign(item, _item);
    let component = null;
    const _xs = Object.hasOwn(item, 'xs') ? item.xs : 12;
    const _sm = Object.hasOwn(item, 'sm') ? item.sm : 12;
    delete item.xs;
    delete item.sm;
    if (item.visible === false) {
      // do nothing
    } else if (item.type == 'text' || item.type == 'date' || item.type == 'month' || item.type == 'decimal') {
      if (Object.hasOwn(item, 'format') && item.format === 'price') {
        item.format = (value) => {
          return formatStringToPrice(value);
        };
      }
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderTextField}
          className="bf-form-field"
          id={'id' + key + item.name}
          {...item}/>
      </Grid>);
    } else if (item.type == 'select') {
      const list = [];
      const selections = item.selections ? item.selections : [];
      let _item = {...item};
      delete _item.type;
      delete _item.blank;
      delete _item.selections;
      if (_item.onChange) {
        item.onChange = _item.onChange;
      }
      if (item.blank) {
        list.push(<MenuItem key={'key' + key + _item.name} value="">&nbsp;</MenuItem>);
      }
      for (const selection of selections) {
        const itemKey = [
          key,
          _item.name,
          selection.cd,
        ].join('-');

        list.push(<MenuItem key={itemKey} value={selection.cd}>
          {selection.cdName}</MenuItem>);
      }
      component = (<Grid key={'key' + key + _item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderSelect}
          className="bf-form-field"
          id={'id' + key + _item.name}
          {..._item}>
          {list}
        </Field>
      </Grid>);
    } else if (item.type == 'positiveButton') {
      const _positiveProps = {
        id: 'id' + item.id,
        onClick: item.click,
        disabled: item.disabled,
        variant: 'contained',
        size: item.size,
        fullWidth: item.fullWidth,
        startIcon: item.startIcon,
      };
      component = (<Grid key={'key' + key + item.id} item xs={_xs} sm={_sm}>
        <PositiveButton
          {..._positiveProps}>
          <span>{item.label}</span>
        </PositiveButton>
      </Grid>);
    } else if (item.type == 'positiveButton2') {
      const _positiveProps = {
        id: 'id' + item.id,
        onClick: item.click,
        disabled: item.disabled,
        variant: 'contained',
        size: item.size,
        fullWidth: item.fullWidth,
        startIcon: item.startIcon,
      };
      component = (<Grid key={'key' + key + item.id} item xs={_xs} sm={_sm}>
        <PositiveButton2
          {..._positiveProps}>
          <span>{item.label}</span>
        </PositiveButton2>
      </Grid>);
    } else if (item.type == 'negativeButton') {
      const _negativeProps = {
        id: 'id' + item.id,
        onClick: item.click,
        disabled: item.disabled,
        variant: 'contained',
        size: item.size,
        fullWidth: item.fullWidth,
        startIcon: item.startIcon,
      };
      component = (<Grid key={'key' + key + item.id} item xs={_xs} sm={_sm}>
        <NegativeButton
          {..._negativeProps}>
          <span>{item.label}</span>
        </NegativeButton>
      </Grid>);
    } else if (item.type == 'checkbox') {
      let tooltip = undefined;
      if (Object.hasOwn(item, 'tooltip')) {
        tooltip = (
          <MuiTooltip content={
            <React.Fragment>
              {item.tooltip.label}
            </React.Fragment>
          }>
            {item.tooltip.icon}
          </MuiTooltip>
        );
      }
      delete item.type;
      if (tooltip) {
        component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
          {createCheckBox([item])}
          {tooltip}
        </Grid>);
      } else {
        component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
          {createCheckBox([item])}
        </Grid>);
      }
    } else if (item.type == 'radiobutton') {
      delete item.type;
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        {createRadioButton([item])}
      </Grid>);
    } else if (item.type == 'fileDL') {
      component = _createFileDownLoad(item);
    } else if (item.type == 'link') {
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderLink}
          className="bf-form-field"
          id={'id' + key + item.name}
          {...item}/>
      </Grid>);
    } else if (item.type == 'simpleLink') {
      component = (
        <Grid key={'key' + key + item.name} item xs={_xs} sm={_sm} style={item.gridStyle}>
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              item.click(e);
            }}
            style={item.linkStyle}
          >
            {item.text}
          </Link>
        </Grid>
      );
    } else if (item.type == 'caution') {
      component = (<Grid key={'key' + key + item.name} items xs={0} sm={_sm} style={{'align-self': 'end'}}>
        <Typography variant="h7" color="error" style={{'margin-left': '1em'}}>{item.label}</Typography>
      </Grid>);
    } else if (item.type == 'dummy') {
      component = (<Grid key={'key' + key + item.name} item xs={0} sm={_sm}></Grid>);
    } else if (item.type == 'multiSelect') {
      const list = [];
      const selections = item.selections ? item.selections : [];
      let _item = {...item};
      delete _item.type;
      delete _item.blank;
      delete _item.selections;
      if (_item.onChange) {
        item.onChange = _item.onChange;
      }
      if (item.blank) {
        list.push(<MenuItem key={'key' + key + _item.name} value="">&nbsp;</MenuItem>);
      }
      for (const selection of selections) {
        const itemKey = [
          key,
          _item.name,
          selection.cd,
        ].join('-');

        list.push(<MenuItem key={itemKey} value={selection.cd}>
          {selection.cdName}</MenuItem>);
      }
      component = (<Grid key={'key' + key + _item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderSelectMultiLine}
          className="bf-form-field"
          id={'id' + key + _item.name}
          {..._item}>
          {list}
        </Field>
      </Grid>);
    } else {
      // do nothing
    }
    return (component);
  });
  return (<Grid
    container
    justifyContent="flex-start"
    alignItems="flex-start"
    spacing={1}>
    {lists}
  </Grid>);
}

export function createCheckBox(components) {
  const lists = components.map((item) =>{
    let _item = {
      key: 'key' + item.name,
      id: item.name,
      name: item.name,
      disabled: item.disabled,
    };
    if (item.onChange) {
      _item.onChange = item.onChange;
    }
    let component =
    ( <Field
      component={renderCheckbox}
      label={<p style={{maxWidth: '34em'}}>
        {item.label}
      </p>}
      {..._item}
    />);
    return (component);
  });
  return (
    <FormControl component="fieldset">
      <FormGroup>
        {lists}
      </FormGroup>
    </FormControl>);
}
export function createRadioButton(components) {
  const lists = components.map((item) =>{
    let _item = {
      key: 'key' + item.name,
      id: item.name,
      name: item.name,
      disabled: item.disabled,
      direction: item.direction || 'vertical',
      defaultValue: item.defaultValue || null,
    };
    if (item.onChange) {
      _item.onChange = item.onChange;
    }

    let component =
    (
      <Field
        component={renderRadioGroup}
        {..._item}
      >
        {item.label.map((label) => {
          return (
            <FormControlLabel
              key={label}
              value={label}
              disabled={item.disabled}
              control={<Radio />}
              label={
                <p style={{color: '#000', maxWidth: '34em', marginBottom: '12px'}}>
                  {label}
                </p>}/>);
        })}
      </Field>

    );
    return (component);
  });

  return (
    <FormControl component="fieldset">
      <FormGroup>
        {lists}
      </FormGroup>
    </FormControl>);
}

export function createSimpleGrid(components, nameSize=6, valueSize=6) {
  const lists = components.map((item) =>{
    let valueItem = <Typography gutterBottom className="bf-textWrap">{item.value}</Typography>;
    if (item.type === 'fileDL') {
      valueItem = (
        <button type="button" className='bf-buttonLink' style={{marginBottom: '1rem'}}
          onClick={() => {
            if (item.onClick) {
              item.onClick();
            }
          }}>
          {item.value}
        </button>
      );
    }
    return (<React.Fragment key={item.name}>
      <Grid item key={item.binding + '1'} xs={nameSize}>
        <Typography gutterBottom>{item.name}</Typography>
      </Grid>
      <Grid item key={item.binding + '2'} xs={valueSize}>
        {valueItem}
      </Grid>
    </React.Fragment>);
  });
  return ( <Grid container>
    {lists}
  </Grid>);
}

export function setSelections(list, label, key, genericMap) {
  const index = findIndex(list,
      (element) => {
        return element.label == label;
      });
  if (index == -1) {
    return;
  }
  list[index].selections = getFilteringMap(genericMap, key);
}

export function setEvent(list, label, event, callback) {
  const index = findIndex(list,
      (element) => {
        return element.label == label;
      });
  if (index == -1) {
    return;
  }
  list[index][event] = callback;
}

export function createFileDownLoad(components) {
  if (!components) {
    return;
  }
  const lists = components.map((item) =>{
    return _createFileDownLoad(item);
  });
  return ( <Grid container>
    {lists}
  </Grid>);
}

function _createFileDownLoad(item) {
  if (item.noLabel) {
    return (
      <Grid item key={item.key} xs={12}>
        <button type="button" className='bf-buttonLink' style={{marginBottom: '1rem'}}
          onClick={() => {
            if (item.onClick) {
              item.onClick();
            }
          }}>
          {item.fileName}
        </button>
      </Grid>
    );
  }
  return (
    <Grid item key={item.key} xs={12}>
      <Typography variant="button" display="block" gutterBottom>
        {item.label}
      </Typography>
      <button type="button" className='bf-buttonLink' style={{marginBottom: '1rem'}}
        onClick={() => {
          if (item.onClick) {
            item.onClick();
          }
        }}>
        {item.fileName}
      </button>
    </Grid>);
}

/**
 * ファイルダウンロードの実行.
 *
 * @param {*} response レスポンス
 */
export function executeFileDownload(response) {
  const blob = new Blob([response.data], {
    type: response.data.type,
  });
  // レスポンスヘッダからファイル名を取得する
  const contentDisposition = response.headers['content-disposition'];
  let fileName = contentDisposition.substring(
      contentDisposition.indexOf('*=') + 3,
      contentDisposition.length - 1,
  );
  // +を半角スペースに置換
  fileName = decodeURI(fileName).replace(/\+/g, ' ');
  if (navigator.msSaveBlob) { // IE
    navigator.msSaveBlob(blob, fileName);
  } else {
    let link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export function createUpload(file) {
  return setParamToComponent(Upload, file);
};

export function setParamToComponent(Component, file) {
  return (<Component
    componentId={file.componentId}
    maxFileCount={file.maxFileCount}
    maxFileSize={file.maxFileSize}
    required={file.required}
    previewWidth={file.previewWidth}
    previewHandler={file.previewHandler}
    acceptFileType={file.acceptFileType}
    fileSetHandler={file.fileSetHandler}
    downloadHandler={file.downloadHandler}
    initFile={file.initFile}
    disabled={file.disabled}
    showShootingDate={file.showShootingDate}
    customChecks={file.customChecks}
    customWarnings={file.customWarnings}
  />);
};

/**
 * 手持箇所名称取得
 * @param {string} orderStatus 申込ステータス
 * @param {array} genericMst 汎用マスタリスト
 * @return {string} 文字列
 */
export function getTemochiKasyo(orderStatus, genericMst) {
  const statusMst = genericMst && (CATEGORY_TYPE.ORDERSTATUS in genericMst) ?
    genericMst[CATEGORY_TYPE.ORDERSTATUS] : [];
  const temochiList = statusMst.filter((data) => data.cd == orderStatus);
  return temochiList.length > 0 ? temochiList[0].valueString3 : '';
};

/**
   * システムエラー
   * @param {*} props
   */
export function sysErr(props) {
  if (!props.doShowMessage) {
    return;
  }
  props.doShowMessage({
    message: 'BFZ000001S',
    action: () => {
      props.navigate('/login');
    },
  });
}

/**
 * 事業場の選択リストを取得する
 * @param {array} zigyojyoList 電柱マスタの事業場リスト
 * @param {string} labelKey 選択リストの表示名を設定する
 * @param {string} valueKey 選択リストの値を設定する
 * @param {string} emptyHeader true:表示リストの先頭行に空白あり, false:先頭行に空白なし
 * @return {array} 事業場の選択リスト
 */
export function createZigyojoDataMap(zigyojyoList, labelKey='cdName', valueKey='cd', emptyHeader=true) {
  let list = [{[labelKey]: '', [valueKey]: ''}];
  if (zigyojyoList === undefined || zigyojyoList === null) {
    return list;
  }

  if (!emptyHeader) {
    list = [];
  }

  for (const data of zigyojyoList) {
    list.push({[labelKey]: data.zigyojoName, [valueKey]: data.zigyojoCd});
  }
  return new DataMap(list, valueKey, labelKey);
}

/**
 * データマップを返す
 * @param {object} genericMap 汎用マスタ
 * @param {string} key バインドキー
 * @param {string} value 値に利用するカラム名
 * @param {string} label ラベルに利用するカラム名
 * @param {Array} filterValues 対象とする値の一覧 絞り込みなしの場合指定しない
 * @return {object} dataMap
 */
export function createDataMap(genericMap, key, value = 'cd', label = 'cdName', filterValues) {
  const data = [{[value]: '', [label]: '　'}];
  if (!genericMap) {
    return new DataMap(data, value, label);
  }
  let items = getFilteringMap(genericMap, key, filterValues);

  let newDataMap = new DataMap(data, value, label);
  if (items) {
    const _newItems = data.concat(items);
    newDataMap = new DataMap(_newItems, value, label);
  }
  return newDataMap;
}

/**
 * 機器設備の架設設備に先行建柱の架設設備を追加して架設設備のデータマップを返す
 * 共架申込で使用する
 * @param {object} genericMap 汎用マスタ
 * @param {Array} filterValues 対象とする値の一覧 絞り込みなしの場合指定しない
 * @return {object} dataMap
 */
export function createKasetuSetubiDataMapForKiki(genericMap, filterValues) {
  const value = 'cd';
  const label = 'cdName';
  const data = [{[value]: '', [label]: '　'}];
  if (!genericMap) {
    return new DataMap(data, value, label);
  }

  // 汎用マスタから機器設備の架設設備を取得する
  const items = getFilteringMap(genericMap, 'kasetuSetubiKikiGairoto', filterValues);

  let senkoKentyuItems = [];
  if (filterValues !== undefined && filterValues !== null &&
    filterValues.length > 0) {
    // 事業者毎架設設備が先行建柱の架設設備を持っているかチェック
    // 先行建柱の架設設備を持っていれば、機器設備用の架設設備に先行建柱の架設設備を追加
    // 先行建柱の架設設備は通信設備の架設設備から取得する
    let kasetuSetubiCds = genericMap[CATEGORY_TYPE.KASETU_SETUBI_CD];
    if (!kasetuSetubiCds) {
      kasetuSetubiCds = [];
    }
    if (filterValues.includes(KASETU_SETUBI_CD.SENKO_KENTYU_KJ)) {
      const kyoJakuCds = kasetuSetubiCds.filter((data) => {
        return data['valueNum1'] === KASETU_SETUBI_TYPE.TUSHIN && data['cd'] === KASETU_SETUBI_CD.SENKO_KENTYU_KJ;
      });
      senkoKentyuItems.push(...kyoJakuCds);
    }
    if (filterValues.includes(KASETU_SETUBI_CD.SENKO_KENTYU_JJ)) {
      const jakuJakuCds = kasetuSetubiCds.filter((data) => {
        return data['valueNum1'] === KASETU_SETUBI_TYPE.TUSHIN && data['cd'] === KASETU_SETUBI_CD.SENKO_KENTYU_JJ;
      });
      senkoKentyuItems.push(...jakuJakuCds);
    }
  }

  let newDataMap = new DataMap(data, value, label);
  if (items) {
    let _newItems = [];
    if (senkoKentyuItems.length > 0) {
      _newItems = data.concat(items).concat(senkoKentyuItems);
    } else {
      _newItems = data.concat(items);
    }
    newDataMap = new DataMap(_newItems, value, label);
  } else {
    if (senkoKentyuItems.length > 0) {
      const _newItems = data.concat(senkoKentyuItems);
      newDataMap = new DataMap(_newItems, value, label);
    }
  }
  return newDataMap;
}

/**
 * 機器設備の架設設備に先行建柱の架設設備を追加して架設設備のデータマップを返す
 * 通信設備の架設設備には先行建柱の架設設備が入っているので、
 * 共架申込（他社柱）で使用する
 * @param {object} genericMap 汎用マスタ
 * @return {object} dataMap
 */
export function createKasetuSetubiNttDataMapForKiki(genericMap) {
  const value = 'cd';
  const label = 'cdName';
  const data = [{[value]: '', [label]: '　'}];
  if (!genericMap) {
    return new DataMap(data, value, label);
  }

  // 汎用マスタから機器設備の架設設備を取得する
  const items = getFilteringMap(genericMap, 'kasetuSetubiCdNttKikiGairoto');

  // 機器設備用の架設設備に先行建柱の架設設備を追加
  // 先行建柱の架設設備は通信設備の架設設備から取得する
  let kasetuSetubiCds = genericMap[CATEGORY_TYPE.KASETU_SETUBI_CD_NTT];
  if (!kasetuSetubiCds) {
    kasetuSetubiCds = [];
  }
  const senkoKentyuItems = kasetuSetubiCds.filter((data) => {
    return data['valueNum1'] === KASETU_SETUBI_TYPE.TUSHIN &&
      (data['cd'] === KASETU_SETUBI_CD_NTT.SENKO_KENTYU_KJ ||
        data['cd'] === KASETU_SETUBI_CD_NTT.SENKO_KENTYU_JJ);
  });

  let newDataMap = new DataMap(data, value, label);
  if (items) {
    let _newItems = [];
    if (senkoKentyuItems.length > 0) {
      _newItems = data.concat(items).concat(senkoKentyuItems);
    } else {
      _newItems = data.concat(items);
    }
    newDataMap = new DataMap(_newItems, value, label);
  } else {
    if (senkoKentyuItems.length > 0) {
      const _newItems = data.concat(senkoKentyuItems);
      newDataMap = new DataMap(_newItems, value, label);
    }
  }
  return newDataMap;
}

/**
   * 選択した電柱の種別によって表示する異動種別を変更する
   * @param {object} dataItem 選択行データ
   * @param {array} dataMapItems 異動種別リスト
   * @return {array} 表示する異動種別リスト
   */
export function getDisplayIdoType(dataItem, dataMapItems) {
  let arr = dataMapItems.filter((data) => data.cd === IDO_TYPE.SHINSETU)
      .map((data) => data.cdName);
  if (dataItem.bukkenNoBukkenMaster !== undefined &&
      dataItem.bukkenNoBukkenMaster !== null) {
    // 料金に影響がでる項目を変更できないようにするために空行を除く
    arr = dataMapItems.filter((data) => data.cd !== IDO_TYPE.SHINSETU)
        .filter((data) => data.cd != '')
        .map((data) => data.cdName);
  }
  return arr;
}

/**
  * 取得元データによって異動種別の表示を変える
  * @param {object} dataItem 対象データ
  * @param {array} dataMapItems 異動種別リスト
  * @return {array} フィルタリングした異動種別リスト
  */
export function getDisplayIdoTypeForKeikan(dataItem, dataMapItems) {
  let arr = dataMapItems.filter((data) => data.cd === IDO_TYPE.SHINSETU)
      .map((data) => data.cdName);
  if (dataItem['kyogaBukkenId']) {
    // 料金に影響がでる項目を変更できないようにするために空行を除く
    arr = dataMapItems.filter((data) => data.cd !== IDO_TYPE.SHINSETU)
        .filter((data) => data.cd != '')
        .map((data) => data.cdName);
  }
  return arr;
};

/**
  * 取得元データによって異動種別の表示を変える
  * @param {object} dataItem 対象データ
  * @param {array} dataMapItems 異動種別リスト（非一束化対応）
  * @return {array} フィルタリングした異動種別リスト
  */
export function getDisplayChargedIdoTypeForKeikan(dataItem, dataMapItems) {
  let arr = dataMapItems.filter((data) =>
    (data.cd === IDO_TYPE.SHINSETU || data.cd === IDO_TYPE.HENKO_ZOJYO))
      .map((data) => data.cdName);
  if (dataItem['kyogaBukkenId']) {
    // 料金に影響がでる項目を変更できないようにするために空行を除く
    arr = dataMapItems.filter((data) => data.cd !== IDO_TYPE.SHINSETU)
        .filter((data) => data.cd !== IDO_TYPE.HENKO_ZOJYO)
        .filter((data) => data.cd != '')
        .map((data) => data.cdName);
  }
  return arr;
};

/**
 * 汎用マスタから線下状況に対応した申込地上高の最大値を取得する
 * @param {object} genericMst 汎用マスタ
 * @param {string} senkaJyokyo 線下状況
 * @return {number} 申込地上高の最大値
 */
export function getMaxTijyoHeight(genericMst, senkaJyokyo) {
  if (genericMst === undefined || genericMst === null ||
    senkaJyokyo === null || senkaJyokyo === '') {
    return 99.99;
  }

  const tijyoMaximumHeight = genericMst['tijyo_maximum_height'] || [];
  const tijyo = tijyoMaximumHeight.filter((d) => d.valueString1 == senkaJyokyo);
  return tijyo.length > 0 ? tijyo[0].valueNum1 : 99.99;
}

/**
 * 汎用マスタから径間最大長を取得する
 * @param {object} genericMst 汎用マスタ
 * @return {number} 径間最大長
 */
export function getMaxKeikanLength(genericMst) {
  if (genericMst === undefined || genericMst === null) {
    return 999.9;
  }

  const max = genericMst['keikan_maximum_length'] || [];
  return max.length > 0 ? max[0].valueNum1 : 999.9;
}

/**
 * 既設設備物件番号の有無で絞り込んだ異動種別を返す
 * @param {*} bukkenNoMaster 既設設備物件番号
 * @param {array} idoTypes 異動種別リスト
 * @return {array} 電柱ID種別で絞り込んだ異動種別リスト
 */
export function getIdoTypePerBukkenNoMaster(bukkenNoMaster, idoTypes) {
  let results = idoTypes.filter((data) => data.cd === IDO_TYPE.SHINSETU);
  if (bukkenNoMaster !== undefined && bukkenNoMaster !== null) {
    results = idoTypes.filter((data) => data.cd !== IDO_TYPE.SHINSETU);
  }
  return results;
}

function setValidate(gmn) {
  let _validate = [];
  if (Object.hasOwn(gmn, 'maxLength')) {
    _validate.push(validateRule.maxLength(gmn.maxLength));
  }
  if (gmn.type == 'date') {
    _validate.push(validateRule.date);
  } else if (gmn.type == 'month') {
    _validate.push(validateRule.month);
  }
  if (gmn.dataType == 'decimal') {
    _validate.push(validateRule.decimal(gmn.precision, gmn.scale));
  }
  if (gmn.dataType == 'datetime') {
    _validate.push(validateRule.datetime());
  }
  return _validate;
}
function setWarn(gmn) {
  let _warn = [];
  const required = !!gmn.required;
  if (required) {
    _warn.push(validateRule.required);
  }
  return _warn;
}
function setItem(gmn, _item) {
  if (gmn.type == 'text') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['disabled'] = !!gmn.disabled;
    _item['required'] = !!gmn.required;
    _item['InputProps'] = {
      readOnly: !!gmn.readOnly,
    };
    if (Object.hasOwn(gmn, 'variant')) {
      _item['variant'] = gmn.variant;
    }
    if (Object.hasOwn(gmn, 'multiline')) {
      _item['multiline'] = gmn.multiline;
      _item['rows'] = gmn.rows;
    }
    if (Object.hasOwn(gmn, 'format')) {
      _item['format'] = gmn.format;
    }
    if (Object.hasOwn(gmn, 'inputFormat')) {
      _item['inputFormat'] = gmn.inputFormat;
    }
    if (Object.hasOwn(gmn, 'price')) {
      _item['price'] = gmn.price;
    }
    if (gmn.autoSize === true) {
      // InputPropsにTextareaAutosizeコンポーネントと設定値を追加
      const inputProps = _item.InputProps;
      inputProps.inputComponent = TextareaAutosize;
      inputProps.inputProps = {
        minRows: gmn.minRows,
        maxRows: gmn.maxRows,
      };
    }
  } else if (gmn.type == 'positiveButton' || gmn.type == 'positiveButton2' || gmn.type == 'negativeButton') {
    _item['label'] = gmn.label;
    _item['id'] = gmn.id;
    _item['variant'] = 'contained';
    _item['size'] = gmn.size;
    _item['disabled'] = !!gmn.disabled;
    _item['click'] = gmn.click;
    if (Object.hasOwn(gmn, 'fullWidth') && gmn.fullWidth !== false) {
      _item['fullWidth'] = true;
    }
    if (Object.hasOwn(gmn, 'startIcon')) {
      _item['startIcon'] = gmn.startIcon;
    }
  } else if (gmn.type == 'select' || gmn.type == 'multiSelect' ) {
    _item['blank'] = gmn.blank;
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['disabled'] = !!gmn.disabled;
    _item['required'] = !!gmn.required;
    _item['InputProps'] = {
      readOnly: !!gmn.readOnly,
    };
    if (Object.hasOwn(gmn, 'selections')) {
      _item['selections'] = gmn.selections;
    } else {
      _item['selections'] = [];
    }
  } else if (gmn.type == 'checkbox' || gmn.type == 'radiobutton') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['disabled'] = !!gmn.disabled;
    if (Object.hasOwn(gmn, 'tooltip')) {
      _item['tooltip'] = gmn.tooltip;
    }
    if (gmn.type == 'radiobutton') {
      _item['direction'] = gmn.direction || 'vertical';
      _item['defaultValue'] = gmn.defaultValue || null;
    }
  } else if (gmn.type == 'date' || gmn.type == 'month') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['disabled'] = !!gmn.disabled;
    _item['required'] = !!gmn.required;
    _item['InputProps'] = {
      readOnly: !!gmn.readOnly,
    };
    _item['InputLabelProps'] = {
      shrink: true,
    };
  } else if (gmn.type == 'fileDL') {
    _item['key'] = gmn.binding;
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fileName'] = gmn.label;
    _item['noLabel'] = gmn.noLabel;
  } else if (gmn.type == 'link') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['click'] = gmn.click;
  } else if (gmn.type == 'simpleLink') {
    _item['name'] = gmn.name;
    _item['text'] = gmn.text;
    _item['fullWidth'] = true;
    _item['click'] = gmn.click;
    _item['gridStyle'] = gmn.gridStyle;
    _item['linkStyle'] = gmn.linkStyle;
  } else if (gmn.type == 'caution') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
  } else if (gmn.type == 'dummy') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
  }
  if (gmn.type != 'fileDL') {
    _item['validate'] = setValidate(gmn);
    _item['warn'] = setWarn(gmn);
  }
  if (!_item['name'] && gmn.name) {
    _item['name'] = gmn.name;
  }
  _item['readOnly'] = !!gmn.readOnly;
}
